import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DirectUpload } from '@rails/activestorage';

// MUI
import IconButton from '@mui/material/IconButton';
import MUITypography from '@mui/material/Typography';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

// MUI Icons
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';

// Components
import Typography from '../Typography';
import Button from '../CustomButton';
import Spinner from '../spinner';
import AddToModal from '../AddTooModal';
import FilePreviewBadge from '../FilePreviewBadge';
import NoteGroup from './NoteGroup';
import LinkRow from './LinkRow';
import FileTypeTooltip from './FileTypeTooltip';

// Styles
import styles from './style.module.scss';
import Divider from '../divider';

// Types
import { APIId, Course, Folder, Note, UploadFile } from '../../types';
import { Link } from './types';

// Utils
import { directUploadUrl } from '../../utils/urls';

interface AttachMaterialsProps {
  header?: React.ReactNode;
  // Current Files (signed ids)
  currentFiles?: string[];
  // Forced Note (if it's not undefined, it will be always displayed and cannot be removed)
  forcedNote?: Note;

  // ===== Handlers =====
  // Sends the signed ids of the uploaded files
  onChangeUploadedFiles?: (files: string[]) => void;
  // Sends the links
  onChangeLinks?: (links: Link[]) => void;
  // Sends the notes
  onChangeNotes?: (notes: Note[]) => void;

  // ===== Classes =====
  classes?: {
    root?: string;
  };
}

const defaultOnChangeUploadedFiles = (files: string[]) => {
}

const defaultOnChangeLinks = (links: Link[]) => {
}

const defaultOnChangeNotes = (notes: Note[]) => {
}

const MAX_LINKS = 10;
const MAX_FILES = 10;
const PDF_MAX_SIZE = 30 * 1024 * 1024; // 30MB in bytes
const OTHER_FILES_MAX_SIZE = 50 * 1024 * 1024; // 50MB in bytes

// Allowed file types
const ALLOWED_FILE_EXTENSIONS = [
  '.pdf',
  '.png', '.jpg', '.jpeg', '.gif', '.svg', '.webp',
  '.doc', '.docx',
  '.txt',
  '.mp3', '.wav', '.m4a', '.aac', '.ogg',
  '.mp4', '.mov', '.avi', '.mkv', '.webm'
];

export const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'image/png', 'image/jpg', 'image/jpeg', 'image/gif', 'image/svg+xml', 'image/webp',
  'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'audio/mpeg', 'audio/wav', 'audio/x-m4a', 'audio/aac', 'audio/ogg',
  'video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/x-matroska', 'video/webm'
];

const DefaultHeader = () => {
  const { t } = useTranslation();

  return (
    <div className={styles.header}>
      <Typography variant='h6' className={styles.header_title}>
        {t('attachMaterials.title')}
      </Typography>
    </div>
  )
}

const AttachMaterials: React.FC<AttachMaterialsProps> = ({
  currentFiles = [],
  forcedNote = undefined,
  onChangeUploadedFiles = defaultOnChangeUploadedFiles,
  onChangeLinks = defaultOnChangeLinks,
  onChangeNotes = defaultOnChangeNotes,
  header = null,
  classes = {},
}) => {
  // Hooks
  const { t } = useTranslation();

  // State
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isFilesOpen, setIsFilesOpen] = useState(false);
  const [showLinkLimitSnackbar, setShowLinkLimitSnackbar] = useState(false);
  const [showFileSizeRejectedSnackbar, setShowFileSizeRejectedSnackbar] = useState(false);
  const [rejectedFiles, setRejectedFiles] = useState<File[]>([]);
  const [showFileTypeRejectedSnackbar, setShowFileTypeRejectedSnackbar] = useState(false);
  const [rejectedFileTypes, setRejectedFileTypes] = useState<File[]>([]);
  const [showFileLimitSnackbar, setShowFileLimitSnackbar] = useState(false);
  const [rejectedFilesCount, setRejectedFilesCount] = useState(0);

  const [showChooseFromSmartNotes, setShowChooseFromSmartNotes] = useState(false);

  // SIGNED IDS OF THE UPLOADED FILES
  const [uploadedFiles, setUploadedFiles] = useState<string[]>(currentFiles);
  const [links, setLinks] = useState<Link[]>([{ id: crypto.randomUUID(), url: '' }]);
  const [selectedNotes, setSelectedNotes] = useState<Note[]>([]);

  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Hooks
  useEffect(() => {
    onChangeUploadedFiles(uploadedFiles);
  }, [uploadedFiles, onChangeUploadedFiles]);

  useEffect(() => {
    onChangeNotes(selectedNotes);
  }, [selectedNotes, onChangeNotes]);

  // Utility functions
  const isPdfFile = (file: File): boolean => {
    return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
  };

  const validateFileSize = (file: File): boolean => {
    const maxSize = isPdfFile(file) ? PDF_MAX_SIZE : OTHER_FILES_MAX_SIZE;
    return file.size <= maxSize;
  };

  const validateFileType = (file: File): boolean => {
    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.substring(fileName.lastIndexOf('.'));

    // Check by MIME type first, then by extension as fallback
    return ALLOWED_MIME_TYPES.includes(file.type) || ALLOWED_FILE_EXTENSIONS.includes(fileExtension);
  };

  // Methods
  const onUploadFiles = useCallback(async (files: File[]) => {
    setIsUploading(true);

    // Check file limit first
    const currentFileCount = getTotalFileCount();
    const availableSlots = MAX_FILES - currentFileCount;

    if (availableSlots <= 0) {
      setRejectedFilesCount(files.length);
      setShowFileLimitSnackbar(true);
      setIsUploading(false);
      return;
    }

    // If we would exceed the limit, only take the files that fit
    const filesToProcess = files.slice(0, availableSlots);
    const rejectedDueToLimit = files.slice(availableSlots);

    if (rejectedDueToLimit.length > 0) {
      setRejectedFilesCount(rejectedDueToLimit.length);
      setShowFileLimitSnackbar(true);
    }

    // Validate files (both type and size)
    const validFiles: File[] = [];
    const invalidSizeFiles: File[] = [];
    const invalidTypeFiles: File[] = [];

    filesToProcess.forEach(file => {
      if (!validateFileType(file)) {
        invalidTypeFiles.push(file);
      } else if (!validateFileSize(file)) {
        invalidSizeFiles.push(file);
      } else {
        validFiles.push(file);
      }
    });

    // Show rejected file type snackbar if there are any
    if (invalidTypeFiles.length > 0) {
      setRejectedFileTypes(invalidTypeFiles);
      setShowFileTypeRejectedSnackbar(true);
    }

    // Show rejected file size snackbar if there are any
    if (invalidSizeFiles.length > 0) {
      setRejectedFiles(invalidSizeFiles);
      setShowFileSizeRejectedSnackbar(true);
    }

    // Only upload valid files
    if (validFiles.length > 0) {
      try {
        const uploadPromises = validFiles.map(file => {
          return new Promise<void>((resolve) => {
            const directUpload = new DirectUpload(file, directUploadUrl());
            directUpload.create((error, attributes) => {
              if (error) {
                console.error(error);
              } else {
                setUploadedFiles(prev => [...prev, attributes?.signed_id || ""]);
              }
              resolve();
            });
          });
        });

        await Promise.all(uploadPromises);
        setIsFilesOpen(true);
      } catch (error) {
        console.error(error);
      }
    }

    setIsUploading(false);
  }, []);

  useEffect(() => {
    if (links.length > 0 && onChangeLinks) onChangeLinks(links.filter(link => link.url.trim() !== ''));
  }, [links, onChangeLinks]);

  // Handlers
  const handleAddLink = useCallback(() => {
    setLinks(prev => {
      if (prev.length >= MAX_LINKS) {
        setShowLinkLimitSnackbar(true);
        return prev;
      }
      return [...prev, { id: crypto.randomUUID(), url: '' }];
    });
  }, []);

  const handleRemoveLink = useCallback((key: string) => {
    setLinks(prev => prev.filter(l => l.id !== key));
  }, []);

  const handleCloseSnackbar = useCallback((_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowLinkLimitSnackbar(false);
  }, []);

  const handleCloseFileSizeSnackbar = useCallback((_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowFileSizeRejectedSnackbar(false);
    setRejectedFiles([]);
  }, []);

  const handleCloseFileTypeSnackbar = useCallback((_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowFileTypeRejectedSnackbar(false);
    setRejectedFileTypes([]);
  }, []);

  const handleCloseFileLimitSnackbar = useCallback((_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowFileLimitSnackbar(false);
    setRejectedFilesCount(0);
  }, []);

  const handleRemoveFile = useCallback((signedId: string) => {
    setUploadedFiles(prev => prev.filter(f => f !== signedId));
  }, []);

  const handleRemoveNote = useCallback((noteId: APIId) => {
    setSelectedNotes(prev => prev.filter(n => n.id !== noteId));
  }, []);

  const handleChangeLink = useCallback((key: string, value: string) => {
    setLinks(prev => prev.map(l => l.id === key ? { ...l, url: value } : l));
  }, []);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      onUploadFiles(files);
      setIsDragging(false);
    }
  }, [onUploadFiles]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      onUploadFiles(files);
    }
  }, [onUploadFiles]);

  const handleAttach = useCallback((folders: Folder[], notes: Note[], courses: Course[]) => {

    setShowChooseFromSmartNotes(false);

    setSelectedNotes(notes);
    onChangeNotes(notes);
    setIsFilesOpen(true);

    onChangeUploadedFiles(uploadedFiles);
  }, []);

  const getTotalFileCount = useCallback(() => {
    let count = 0;
    if (forcedNote) count += forcedNote.file_sources.length;
    if (selectedNotes) {
      selectedNotes.forEach(note => {
        count += note.file_sources.length;
      });
    }
    if (uploadedFiles) count += uploadedFiles.length;

    return count;
  }, [forcedNote, selectedNotes, uploadedFiles]);

  const hasFiles = !!forcedNote || getTotalFileCount() > 0;

  // Memoize the startingNotes array to prevent infinite loops in AddTooModal
  const startingNotes = useMemo(() => selectedNotes.map(note => note.id), [selectedNotes]);

  return (
    <div className={`${styles.root} ${classes.root}`}>
      {header || <DefaultHeader />}

      <div
        className={`${styles.file_upload_container} ${isDragging ? styles.file_upload_container_dragging : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isUploading && <Spinner />}

        <Typography variant='p' className={styles.file_upload_text}>
          <span className={styles.file_upload_text_clickable} onClick={() => fileInputRef.current?.click()}>
            {t('attachMaterials.upload_file')}
          </span>

          {t('attachMaterials.or_drag_and_drop')}
        </Typography>

        <MUITypography variant='body2' className={styles.file_upload_text} color='textSecondary'>
          {t('attachMaterials.max_files')}
        </MUITypography>


        <MUITypography variant='body2' className={styles.file_upload_text} color='textSecondary'>
          {t('attachMaterials.file_types.part1')}

          <FileTypeTooltip />
        </MUITypography>

        <Button
          variant='secondary'
          classes={{ root: styles.choose_notes_button }}
          onClick={() => setShowChooseFromSmartNotes(true)}
        >
          {t('attachMaterials.choose_from_smart_notes')}
        </Button>

        <input
          ref={fileInputRef}
          type='file'
          multiple
          accept=".pdf,.png,.jpg,.jpeg,.gif,.svg,.webp,.doc,.docx,.txt,.mp3,.wav,.m4a,.aac,.ogg,.mp4,.mov,.avi,.mkv,.webm"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </div>

      <div className={styles.links_container}>
        <Typography variant='p' className={styles.links_container_title}>
          {t('attachMaterials.links_title')}

          <span className={styles.links_container_count}>
            ({t('attachMaterials.max_10')})
          </span>
        </Typography>

        {links.map(link => (
          <LinkRow
            key={link.id}
            id={link.id}
            url={link.url}
            onRemove={handleRemoveLink}
            onLinkChange={handleChangeLink}
            hasMore={links.length > 1}
          />
        ))}

        {links.length < MAX_LINKS && (
          <Button variant='secondary' classes={{ root: `${styles.links_container_add_link} dark:text-white` }} onClick={handleAddLink}>
            {`+ ${t('attachMaterials.add_another_one')}`}
          </Button>
        )}
      </div>

      <div className={styles.files_header}>
        <Typography variant='p' className={styles.header_title}>
          {t('attachMaterials.files')}

          {hasFiles && (
            <span className={styles.files_header_count}>
              ({getTotalFileCount()})
            </span>
          )}
        </Typography>

        <IconButton onClick={() => setIsFilesOpen(!isFilesOpen)}>
          {isFilesOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
        </IconButton>
      </div>

      <Divider />

      <div className={styles.files} style={{ display: isFilesOpen ? 'flex' : 'none' }}>
        {!hasFiles && (
          <MUITypography variant='body1' color='textSecondary' className={styles.files_empty}>
            {t('attachMaterials.no_files_or_notes')}
          </MUITypography>
        )}

        {forcedNote && <NoteGroup note={forcedNote} />}

        {selectedNotes.map(note => <NoteGroup note={note} onRemove={handleRemoveNote} />)}

        {uploadedFiles.map(file => (<FilePreviewBadge fileId={file} onRemove={handleRemoveFile} />))}
      </div>

      <AddToModal
        type='attach'
        title={t('attachMaterials.choose_from_smart_notes')}
        open={showChooseFromSmartNotes}
        onClose={() => setShowChooseFromSmartNotes(false)}
        onAttach={handleAttach}
        startingNotes={startingNotes}
      />

      <Snackbar
        open={showLinkLimitSnackbar}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="warning" variant="filled">
          {t('attachMaterials.max_links_reached')}
        </Alert>
      </Snackbar>

      <Snackbar
        open={showFileSizeRejectedSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseFileSizeSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseFileSizeSnackbar} severity="info" variant="filled">
          {rejectedFiles.length === 1
            ? t('attachMaterials.file_size_rejected_single', { fileName: rejectedFiles[0]?.name })
            : t('attachMaterials.file_size_rejected', { count: rejectedFiles.length })
          }
        </Alert>
      </Snackbar>

      <Snackbar
        open={showFileTypeRejectedSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseFileTypeSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseFileTypeSnackbar} severity="info" variant="filled">
          {rejectedFileTypes.length === 1
            ? t('attachMaterials.file_type_rejected_single', { fileName: rejectedFileTypes[0]?.name })
            : t('attachMaterials.file_type_rejected', { count: rejectedFileTypes.length })
          }
        </Alert>
      </Snackbar>

      <Snackbar
        open={showFileLimitSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseFileLimitSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseFileLimitSnackbar} severity="info" variant="filled">
          {rejectedFilesCount === 1
            ? t('attachMaterials.file_limit_rejected_single', { maxFiles: MAX_FILES })
            : t('attachMaterials.file_limit_rejected', { count: rejectedFilesCount, maxFiles: MAX_FILES })
          }
        </Alert>
      </Snackbar>
    </div>
  )
}

export default AttachMaterials;
